import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface PanelUser {
  id: string;
  email: string;
  displayName: string | null;
  isPanelAdmin: boolean;
}

export interface ImpersonatorContext {
  // The original admin's session, stashed so we can restore it on "stop".
  accessToken: string;
  refreshToken: string;
  user: PanelUser;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: PanelUser | null;
  impersonator: ImpersonatorContext | null;
  setTokens: (t: { accessToken: string; refreshToken: string; user?: PanelUser | null }) => void;
  setUser: (u: PanelUser | null) => void;
  startImpersonating: (t: { accessToken: string; user: PanelUser }) => void;
  stopImpersonating: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      impersonator: null,
      setTokens: ({ accessToken, refreshToken, user }) =>
        set((s) => ({
          accessToken,
          refreshToken,
          user: user ?? s.user,
        })),
      setUser: (user) => set({ user }),
      startImpersonating: ({ accessToken, user }) =>
        set((s) => {
          // Refuse to nest impersonation sessions — the admin must stop the
          // current one first.
          if (s.impersonator) return s;
          if (!s.accessToken || !s.refreshToken || !s.user) return s;
          return {
            impersonator: {
              accessToken: s.accessToken,
              refreshToken: s.refreshToken,
              user: s.user,
            },
            accessToken,
            // Impersonation sessions have no refresh token — they expire with
            // the access token.
            refreshToken: null,
            user,
          };
        }),
      stopImpersonating: () =>
        set((s) => {
          if (!s.impersonator) return s;
          return {
            accessToken: s.impersonator.accessToken,
            refreshToken: s.impersonator.refreshToken,
            user: s.impersonator.user,
            impersonator: null,
          };
        }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null, impersonator: null }),
    }),
    { name: "admincp-auth" },
  ),
);
